import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, CircularProgress, Alert, Chip, Divider, Dialog, DialogContent, DialogActions, DialogTitle, Button, ImageList, ImageListItem, useTheme, useMediaQuery, IconButton, Tooltip, Snackbar } from '@mui/material';
import { Delete as DeleteIcon, Event as EventIcon } from '@mui/icons-material';
import EventForm from './EventForm';
import { collection, query, getDocs, orderBy, Timestamp, where, doc, deleteDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { Event, EVENT_ZONES } from '../types/Event';
import { useAuth } from '../contexts/AuthContext';

interface OrphanedPhotosDatesProps {
  title?: string;
}

interface Photo {
  id?: string;
  date: Timestamp;
  url: string;
  path: string;
  eventId?: string;
  storagePath?: string;
}

const OrphanedPhotosDates: React.FC<OrphanedPhotosDatesProps> = ({ title = "Dates des photos orphelines" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [orphanedDates, setOrphanedDates] = useState<string[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  
  // États pour le dialogue d'affichage des photos
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedDatePhotos, setSelectedDatePhotos] = useState<Photo[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState<boolean>(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  
  // États pour la suppression des photos
  const [deletingPhoto, setDeletingPhoto] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  
  // États pour la création d'événement
  const [eventFormOpen, setEventFormOpen] = useState<boolean>(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 1. Récupérer tous les événements pour avoir leurs dates
        const eventsRef = collection(db, 'events');
        const eventsSnapshot = await getDocs(eventsRef);
        const eventsData: Event[] = [];
        
        eventsSnapshot.forEach(doc => {
          const eventData = { id: doc.id, ...doc.data() } as Event;
          eventsData.push(eventData);
        });
        
        setEvents(eventsData);
        
        // 2. Récupérer toutes les photos depuis Firestore
        const photosRef = collection(db, 'photos');
        const photosQuery = query(photosRef, orderBy('date', 'desc'));
        const photosSnapshot = await getDocs(photosQuery);
        
        // Créer un Map pour regrouper les photos par date (juste la partie date, sans l'heure)
        const photosByDate = new Map<string, number>();
        const orphanedPhotosByDate = new Map<string, number>();
        
        photosSnapshot.forEach(doc => {
          const photoData = { id: doc.id, ...doc.data() } as Photo;
          
          if (photoData.date) {
            // Convertir le Timestamp en Date
            let photoDate: Date;
            if (photoData.date instanceof Timestamp) {
              photoDate = photoData.date.toDate();
            } else if (typeof photoData.date === 'string') {
              photoDate = new Date(photoData.date);
            } else {
              // Si le format n'est pas reconnu, passer à la photo suivante
              return;
            }
            
            // Extraire juste la date (sans l'heure)
            const dateKey = photoDate.toISOString().split('T')[0];
            
            // Incrémenter le compteur pour cette date
            photosByDate.set(dateKey, (photosByDate.get(dateKey) || 0) + 1);
            
            // Vérifier si cette photo correspond à un événement
            // Vérifier d'abord si la photo a un eventId spécifique
            if (photoData.eventId) {
              // Vérifier si cet événement existe encore
              const eventExists = eventsData.some(event => event.id === photoData.eventId);
              if (!eventExists) {
                // L'événement a été supprimé, donc la photo est orpheline
                orphanedPhotosByDate.set(dateKey, (orphanedPhotosByDate.get(dateKey) || 0) + 1);
              }
            } else {
              // Si pas d'eventId, vérifier par date comme avant
              const matchingEvent = eventsData.find(event => {
                if (!event.startDate || !event.endDate) return false;
                
                const eventStart = new Date(event.startDate);
                const eventEnd = new Date(event.endDate);
                
                // Ajouter une marge de 12 heures avant et après l'événement
                eventStart.setHours(eventStart.getHours() - 12);
                eventEnd.setHours(eventEnd.getHours() + 12);
                
                return photoDate >= eventStart && photoDate <= eventEnd;
              });
              
              // Si aucun événement ne correspond, c'est une photo orpheline
              if (!matchingEvent) {
                orphanedPhotosByDate.set(dateKey, (orphanedPhotosByDate.get(dateKey) || 0) + 1);
              }
            }
          }
        });
        
        // Convertir le Map en tableau pour l'affichage
        const orphanedDatesArray: string[] = [];
        
        orphanedPhotosByDate.forEach((count, dateStr) => {
          // Format: "2023-05-15 (12 photos)"
          orphanedDatesArray.push(`${dateStr} (${count} photo${count > 1 ? 's' : ''})`);
        });
        
        console.log('Dates orphelines trouvées:', orphanedDatesArray);
        
        // Trier les dates orphelines par ordre décroissant (plus récent en premier)
        orphanedDatesArray.sort((a, b) => {
          const dateA = a.split(' ')[0];
          const dateB = b.split(' ')[0];
          return dateB.localeCompare(dateA);
        });
        
        setOrphanedDates(orphanedDatesArray);
      } catch (err) {
        console.error('Erreur lors de la recherche des photos orphelines:', err);
        setError('Erreur lors de la recherche des photos orphelines');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Formater une date pour l'affichage
  const formatDate = (dateStr: string) => {
    // Format d'entrée: "2023-05-15 (12 photos)"
    const parts = dateStr.split(' ');
    const date = parts[0];
    const countPart = parts.slice(1).join(' '); // Pour gérer le cas où il y a des espaces dans la partie compteur
    
    const [year, month, day] = date.split('-');
    return `${day}/${month}/${year} ${countPart}`;
  };
  
  // Fonction pour recharger les données
  const refreshData = () => {
    setLoading(true);
    setError(null);
    // Réinitialiser l'état pour forcer le useEffect à s'exécuter à nouveau
    setOrphanedDates([]);
    
    // Forcer le rechargement des données
    const fetchData = async () => {
      try {
        // Récupérer tous les événements pour avoir leurs dates
        const eventsRef = collection(db, 'events');
        const eventsSnapshot = await getDocs(eventsRef);
        const eventsData: Event[] = [];
        
        eventsSnapshot.forEach(doc => {
          const eventData = { id: doc.id, ...doc.data() } as Event;
          eventsData.push(eventData);
        });
        
        setEvents(eventsData);
        
        // Récupérer toutes les photos depuis Firestore
        const photosRef = collection(db, 'photos');
        const photosQuery = query(photosRef, orderBy('date', 'desc'));
        const photosSnapshot = await getDocs(photosQuery);
        
        // Créer un Map pour regrouper les photos par date (juste la partie date, sans l'heure)
        const photosByDate = new Map<string, number>();
        const orphanedPhotosByDate = new Map<string, number>();
        
        photosSnapshot.forEach(doc => {
          const photoData = doc.data() as Photo;
          
          if (photoData.date) {
            // Convertir le Timestamp en Date
            let photoDate: Date;
            if (photoData.date instanceof Timestamp) {
              photoDate = photoData.date.toDate();
            } else if (typeof photoData.date === 'string') {
              photoDate = new Date(photoData.date);
            } else {
              // Si le format n'est pas reconnu, passer à la photo suivante
              return;
            }
            
            // Extraire juste la date (sans l'heure)
            const dateKey = photoDate.toISOString().split('T')[0];
            
            // Incrémenter le compteur pour cette date
            photosByDate.set(dateKey, (photosByDate.get(dateKey) || 0) + 1);
            
            // Vérifier si cette photo correspond à un événement
            const matchingEvent = eventsData.find(event => {
              if (!event.startDate || !event.endDate) return false;
              
              const eventStart = new Date(event.startDate);
              const eventEnd = new Date(event.endDate);
              
              // Ajouter une marge de 12 heures avant et après l'événement
              eventStart.setHours(eventStart.getHours() - 12);
              eventEnd.setHours(eventEnd.getHours() + 12);
              
              return photoDate >= eventStart && photoDate <= eventEnd;
            });
            
            // Si aucun événement ne correspond, c'est une photo orpheline
            if (!matchingEvent) {
              orphanedPhotosByDate.set(dateKey, (orphanedPhotosByDate.get(dateKey) || 0) + 1);
            }
          }
        });
        
        // Convertir le Map en tableau pour l'affichage
        const orphanedDatesArray: string[] = [];
        
        orphanedPhotosByDate.forEach((count, dateStr) => {
          // Format: "2023-05-15 (12 photos)"
          orphanedDatesArray.push(`${dateStr} (${count} photo${count > 1 ? 's' : ''})`);
        });
        
        // Trier les dates orphelines par ordre décroissant (plus récent en premier)
        orphanedDatesArray.sort((a, b) => {
          const dateA = a.split(' ')[0];
          const dateB = b.split(' ')[0];
          return dateB.localeCompare(dateA);
        });
        
        setOrphanedDates(orphanedDatesArray);
      } catch (err) {
        console.error('Erreur lors de la recherche des photos orphelines:', err);
        setError('Erreur lors de la recherche des photos orphelines');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  };
  
  // Fonction pour récupérer les photos d'une date spécifique
  const fetchPhotosByDate = async (dateStr: string) => {
    try {
      setLoadingPhotos(true);
      setSelectedDatePhotos([]);
      
      // Extraire la date du format "YYYY-MM-DD (X photos)"
      const date = dateStr.split(' ')[0];
      
      // Créer des objets Date pour le début et la fin de la journée
      const startDate = new Date(`${date}T00:00:00`);
      const endDate = new Date(`${date}T23:59:59`);
      
      console.log(`Recherche des photos entre ${startDate.toISOString()} et ${endDate.toISOString()}`);
      
      // Récupérer les photos depuis Firestore
      const photosRef = collection(db, 'photos');
      const photosQuery = query(
        photosRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );
      
      const photosSnapshot = await getDocs(photosQuery);
      const photosData: Photo[] = [];
      
      photosSnapshot.forEach(doc => {
        const photoData = doc.data() as Photo;
        photosData.push({
          ...photoData,
          id: doc.id
        } as any);
      });
      
      console.log(`Trouvé ${photosData.length} photos pour la date ${date}`);
      setSelectedDatePhotos(photosData);
    } catch (err) {
      console.error('Erreur lors de la récupération des photos:', err);
      setError('Erreur lors de la récupération des photos');
    } finally {
      setLoadingPhotos(false);
    }
  };
  
  // Fonction pour gérer le clic sur une date
  const handleDateClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    fetchPhotosByDate(dateStr);
  };
  
  // Fonction pour fermer le dialogue
  const handleCloseDialog = () => {
    // Conserver la date sélectionnée pendant un court instant pour éviter les problèmes d'affichage
    setTimeout(() => {
      setSelectedDate(null);
      setSelectedDatePhotos([]);
    }, 100);
  };
  
  // Fonction pour afficher une photo en grand
  const handlePhotoClick = (url: string) => {
    setSelectedPhoto(url);
  };
  
  // Fonction pour fermer la vue agrandie
  const handleClosePhotoView = () => {
    setSelectedPhoto(null);
  };
  
  // Fonction pour ouvrir le dialogue de confirmation de suppression
  const confirmDeletePhoto = (photo: Photo) => {
    setPhotoToDelete(photo);
    setConfirmDialogOpen(true);
  };
  
  // Fonction pour fermer le dialogue de confirmation
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setPhotoToDelete(null);
  };
  
  // Fonction pour supprimer une photo (après confirmation)
  const handleDeletePhoto = async () => {
    if (!photoToDelete || !photoToDelete.id || !photoToDelete.path) {
      setSnackbarMessage('Impossible de supprimer cette photo : informations manquantes');
      setSnackbarOpen(true);
      handleCloseConfirmDialog();
      return;
    }
    
    try {
      setDeletingPhoto(photoToDelete.id);
      handleCloseConfirmDialog(); // Fermer le dialogue de confirmation
      
      // 1. Supprimer le document de Firestore
      await deleteDoc(doc(db, 'photos', photoToDelete.id));
      
      // 2. Supprimer le fichier de Storage
      const storageRef = ref(storage, photoToDelete.path);
      await deleteObject(storageRef);
      
      // 3. Mettre à jour la liste des photos
      setSelectedDatePhotos(prev => prev.filter(p => p.id !== photoToDelete.id));
      
      // 4. Afficher un message de succès
      setSnackbarMessage('Photo supprimée avec succès');
      setSnackbarOpen(true);
      
      // 5. Si c'était la dernière photo pour cette date, rafraîchir la liste des dates
      if (selectedDatePhotos.length <= 1) {
        handleCloseDialog();
        refreshData();
      }
    } catch (err) {
      console.error('Erreur lors de la suppression de la photo:', err);
      setSnackbarMessage('Erreur lors de la suppression de la photo');
      setSnackbarOpen(true);
    } finally {
      setDeletingPhoto(null);
      setPhotoToDelete(null);
    }
  };
  
  // Fonction pour fermer le snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  
  // Fonction pour ouvrir le formulaire de création d'événement
  const handleOpenEventForm = () => {
    setEventFormOpen(true);
  };
  
  // Fonction pour fermer le formulaire de création d'événement
  const handleCloseEventForm = () => {
    setEventFormOpen(false);
  };
  
  // Fonction appelée après la création d'un événement
  const handleEventSaved = () => {
    setEventFormOpen(false);
    setSnackbarMessage('Événement créé avec succès');
    setSnackbarOpen(true);
    // Rafraîchir les données après la création d'un événement
    refreshData();
  };
  
  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 3, 
        mt: 3, 
        mb: 3, 
        backgroundColor: 'rgba(30, 30, 30, 0.7)', 
        color: '#fff',
        borderRadius: 2
      }}
    >
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Divider sx={{ mb: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress color="info" />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : orphanedDates.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Aucune photo orpheline trouvée. Toutes les photos correspondent à des événements connus.
        </Alert>
      ) : (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {orphanedDates.map((dateStr, index) => (
            <Chip
              key={index}
              label={formatDate(dateStr)}
              color="primary"
              variant="outlined"
              onClick={() => handleDateClick(dateStr)}
              sx={{ 
                m: 0.5, 
                borderColor: '#3498db',
                color: '#fff',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(52, 152, 219, 0.2)',
                }
              }}
            />
          ))}
        </Box>
      )}
      
      {/* Dialogue pour afficher les photos d'une date spécifique */}
      <Dialog
        open={!!selectedDate}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        TransitionProps={{
          onExited: () => {
            // Réinitialiser complètement les états après la fin de l'animation de fermeture
            setSelectedDate(null);
            setSelectedDatePhotos([]);
          }
        }}
      >
        <DialogContent sx={{ bgcolor: '#1e1e1e', color: '#fff', p: 3 }}>
          {selectedDate && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Photos du {formatDate(selectedDate)}
              </Typography>
              {isAdmin && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EventIcon />}
                  onClick={handleOpenEventForm}
                  sx={{ ml: 2 }}
                >
                  Créer un événement
                </Button>
              )}
            </Box>
          )}
          
          {loadingPhotos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
              <CircularProgress color="info" />
            </Box>
          ) : selectedDatePhotos.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Aucune photo trouvée pour cette date.
            </Alert>
          ) : (
            <ImageList cols={isMobile ? 1 : 3} gap={16}>
              {selectedDatePhotos.map((photo, index) => (
                <ImageListItem 
                  key={index} 
                  sx={{ 
                    position: 'relative',
                    '&:hover .delete-button': { opacity: 1 }
                  }}
                >
                  <img
                    src={photo.url}
                    alt={`Photo ${index + 1}`}
                    loading="lazy"
                    style={{ 
                      width: '100%', 
                      height: 'auto',
                      maxHeight: '300px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handlePhotoClick(photo.url)}
                  />
                  <Tooltip title="Supprimer cette photo">
                    <IconButton 
                      className="delete-button"
                      color="error"
                      size="small"
                      disabled={deletingPhoto === photo.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDeletePhoto(photo);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        },
                        '&.Mui-disabled': {
                          opacity: 0.5,
                          backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        }
                      }}
                    >
                      {deletingPhoto === photo.id ? 
                        <CircularProgress size={20} color="inherit" /> : 
                        <DeleteIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#1e1e1e', color: '#fff' }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#3498db' }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue pour afficher une photo en grand */}
      <Dialog
        open={!!selectedPhoto}
        onClose={handleClosePhotoView}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent sx={{ p: 0, overflow: 'hidden', textAlign: 'center', bgcolor: '#000' }}>
          {selectedPhoto && (
            <img 
              src={selectedPhoto} 
              alt="Photo agrandie" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: 'calc(100vh - 64px)',
                objectFit: 'contain'
              }} 
            />
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#000', color: '#fff' }}>
          <Button onClick={handleClosePhotoView} sx={{ color: '#fff' }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue de confirmation de suppression */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { bgcolor: '#1e1e1e', color: '#fff' }
        }}
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Confirmation de suppression
          </Typography>
          <Typography variant="body1">
            Êtes-vous sûr de vouloir supprimer définitivement cette photo ? Cette action est irréversible.
          </Typography>
          {photoToDelete?.url && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <img 
                src={photoToDelete.url} 
                alt="Photo à supprimer" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '200px',
                  objectFit: 'contain',
                  borderRadius: '4px'
                }} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseConfirmDialog} 
            sx={{ color: '#3498db' }}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleDeletePhoto} 
            variant="contained" 
            color="error"
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Dialogue pour le formulaire de création d'événement */}
      <Dialog
        open={eventFormOpen}
        onClose={handleCloseEventForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Créer un événement pour les photos du {selectedDate ? formatDate(selectedDate) : ''}
        </DialogTitle>
        <DialogContent>
          <EventForm 
            onClose={handleCloseEventForm} 
            onSave={handleEventSaved} 
            event={{
              // Préremplir avec la date des photos orphelines
              startDate: selectedDate ? (() => {
                // Créer une date locale à 10h00 (sans conversion de fuseau horaire)
                const dateStr = selectedDate.split(' ')[0];
                const [year, month, day] = dateStr.split('-').map(Number);
                return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T10:00`;
              })() : '',
              // Date de fin = date de prise de vue + 1 jour à 03h00
              endDate: selectedDate ? (() => {
                // Créer une date locale à 03h00 le jour suivant (sans conversion de fuseau horaire)
                const dateStr = selectedDate.split(' ')[0];
                const [year, month, day] = dateStr.split('-').map(Number);
                // Créer une date et ajouter un jour
                const nextDay = new Date(year, month - 1, day + 1);
                return `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}T03:00`;
              })() : '',
              title: '',
              location: '',
              maxParticipants: 10,
              participants: [],
              zone: EVENT_ZONES.COMMUNAL // Valeur par défaut pour la zone (salle communale)
            }}
          />
        </DialogContent>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default OrphanedPhotosDates;
